import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const AlternativeOptionsSection = ({ recommendation }) => {
  const [alternatives, setAlternatives] = useState([]);

  useEffect(() => {
    generateAlternatives();
  }, [recommendation]);

  const generateAlternatives = () => {
    const alts = [];
    const primaryModality = recommendation?.recommendedModality?.toUpperCase() || recommendation?.primaryModality?.toUpperCase();
    const bodyRegion = recommendation?.bodyRegion || '';

    // If primary ≠ MRI, suggest MRI
    if (!primaryModality?.includes('MRI')) {
      alts?.push({
        modality: `MRI ${bodyRegion}`,
        contrast: 'WITH or WITHOUT (as appropriate)',
        radiation: 'ZERO',
        radiationBadge: 'zero',
        pros: [
          'Zero radiation - completely safe',
          'Excellent soft tissue detail',
          'Safe for repeated imaging',
          'Superior for soft tissue, nerves, joints'
        ],
        cons: [
          'Longer scan time (30-60 min)',
          'More expensive',
          'Contraindicated with some metal implants',
          'May require sedation for claustrophobia'
        ],
        whenToUse: getMRIIndication(bodyRegion),
        priority: 1
      });
    }

    // Suggest ultrasound if applicable
    if (shouldSuggestUltrasound(bodyRegion, recommendation)) {
      alts?.push({
        modality: `Ultrasound ${bodyRegion}`,
        contrast: 'None',
        radiation: 'ZERO',
        radiationBadge: 'zero',
        pros: [
          'Zero radiation',
          'Real-time imaging',
          'Portable',
          'No contraindications'
        ],
        cons: [
          'Operator dependent',
          'Limited by obesity',
          'Cannot penetrate bone/air'
        ],
        whenToUse: getUltrasoundIndication(bodyRegion),
        priority: 2
      });
    }

    // If CT, suggest low-dose
    if (primaryModality?.includes('CT')) {
      alts?.push({
        modality: `Low-Dose CT ${bodyRegion}`,
        contrast: recommendation?.contrastDetails || 'As per primary recommendation',
        radiation: 'REDUCED',
        radiationBadge: 'low',
        pros: [
          'Up to 50% less radiation',
          'Fast imaging',
          'Good for follow-up'
        ],
        cons: [
          'Still uses radiation',
          'Slightly lower quality'
        ],
        whenToUse: 'When CT necessary but radiation is a concern (pediatric, young adults)',
        priority: 3
      });
    }

    setAlternatives(alts?.sort((a, b) => a?.priority - b?.priority));
  };

  const getMRIIndication = (bodyRegion) => {
    const region = bodyRegion?.toLowerCase() || '';
    if (region?.includes('brain')) return 'Superior for tumors, stroke (>24hrs), MS, pituitary';
    if (region?.includes('spine')) return 'Gold standard for disc herniation, cord compression';
    if (region?.includes('knee') || region?.includes('joint')) return 'Excellent for ligaments, meniscus, cartilage';
    return 'Excellent soft tissue detail without radiation';
  };

  const getUltrasoundIndication = (bodyRegion) => {
    const region = bodyRegion?.toLowerCase() || '';
    if (region?.includes('abdomen')) return 'Excellent for gallstones, liver, kidneys, pregnancy';
    if (region?.includes('thyroid')) return 'First-line for thyroid nodules, neck masses';
    if (region?.includes('scrotum')) return 'Gold standard for testicular evaluation';
    return 'Good for superficial structures and vascular assessment';
  };

  const shouldSuggestUltrasound = (bodyRegion, rec) => {
    const region = bodyRegion?.toLowerCase() || '';
    const modality = (rec?.recommendedModality || rec?.primaryModality || '')?.toLowerCase();
    if (modality?.includes('ultrasound')) return false;
    return region?.includes('abdomen') || region?.includes('pelvis') || region?.includes('thyroid') || region?.includes('scrotum');
  };

  if (alternatives?.length === 0) return null;

  return (
    <div className="alternative-options-section bg-card border border-border rounded-lg p-6 mt-6">
      <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
        <Icon name="GitBranch" size={20} color="var(--color-primary)" />
        🔄 Alternative Imaging Options
      </h3>
      <p className="subtitle text-sm text-muted-foreground mb-4">
        Consider radiation-free alternatives when appropriate
      </p>
      <div className="radiation-priority-notice bg-warning/10 border border-warning/20 rounded-lg p-4 mb-6 flex items-start gap-3">
        <span className="icon text-2xl">☢️</span>
        <div>
          <strong className="text-foreground block mb-1">Radiation Safety Priority</strong>
          <p className="text-sm text-muted-foreground">
            Alternatives prioritize MRI/ultrasound (zero radiation) to minimize exposure.
          </p>
        </div>
      </div>
      <div className="alternatives-grid grid grid-cols-1 md:grid-cols-2 gap-4">
        {alternatives?.map((alt, idx) => (
          <div
            key={idx}
            className={`alternative-card priority-${alt?.priority} bg-gradient-to-br ${alt?.radiationBadge === 'zero' ? 'from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-2 border-green-300 dark:border-green-700' : 'from-yellow-50 to-yellow-100 dark:from-yellow-900/20 dark:to-yellow-800/20 border-2 border-yellow-300 dark:border-yellow-700'} rounded-lg p-5`}
          >
            <div className="alt-header flex items-start justify-between mb-3">
              <h4 className="text-base font-semibold text-foreground">{alt?.modality}</h4>
              <span
                className={`radiation-badge ${alt?.radiationBadge} px-3 py-1 rounded-lg text-xs font-bold ${alt?.radiation === 'ZERO' ? 'bg-green-500 text-white' : 'bg-yellow-500 text-white'}`}
              >
                {alt?.radiation === 'ZERO' ? '☢️✅ ZERO Radiation' : `☢️⚠️ ${alt?.radiation} Radiation`}
              </span>
            </div>

            <div className="alt-details mb-3">
              <span className="text-sm text-muted-foreground">
                <strong>Contrast:</strong> {alt?.contrast}
              </span>
            </div>

            <div className="pros mb-3">
              <h5 className="text-sm font-semibold text-foreground mb-2">✅ Advantages:</h5>
              <ul className="space-y-1">
                {alt?.pros?.map((pro, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                    <span className="text-green-500 mt-0.5">•</span>
                    <span>{pro}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="cons mb-3">
              <h5 className="text-sm font-semibold text-foreground mb-2">⚠️ Considerations:</h5>
              <ul className="space-y-1">
                {alt?.cons?.map((con, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                    <span className="text-yellow-500 mt-0.5">•</span>
                    <span>{con}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="when-to-use bg-white/50 dark:bg-gray-800/50 rounded-lg p-3 mb-3">
              <h5 className="text-sm font-semibold text-foreground mb-1">📋 When to Consider:</h5>
              <p className="text-xs text-muted-foreground">{alt?.whenToUse}</p>
            </div>

            {alt?.priority === 1 && (
              <div className="priority-badge bg-green-500 text-white px-3 py-1.5 rounded-lg text-xs font-bold text-center">
                ⭐ Radiation-Free Alternative
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="clinical-note bg-muted rounded-lg p-4 mt-6">
        <strong className="text-foreground block mb-2">Clinical Decision:</strong>
        <p className="text-sm text-muted-foreground">
          While alternatives minimize radiation, recommended modality was chosen for clinical appropriateness and diagnostic yield. Discuss radiation risks vs. benefits with patient.
        </p>
      </div>
    </div>
  );
};

export default AlternativeOptionsSection;